import type { PropsWithChildren, ReactElement } from 'react';
import React from 'react';
import GlobalPageLayout from 'src/components/layout/global-page-layout';
import type { PageContext, PageLayoutOptions } from 'src/components/common/page-context';
import { PageLayoutType } from 'src/components/common/page-context';
import SiteProvider from 'src/context/site-provider';
import ClubPageLayout from 'src/components/layout/club-page-layout';
import type { TemplatePageContext } from 'src/components/common/template-page-context';
import type { PageProps } from 'gatsby';
import { SimplePageLayout } from 'src/components/layout/simple-page-layout';
import { BuildType } from 'src/enums/build/build-type';
import Helmet from 'react-helmet';
import CareerPageLayout from './career-page-layout';

export type LayoutProps = PropsWithChildren<{
  props: PageProps<unknown, PageContext | TemplatePageContext, unknown>;
}>;

// Matches /clubs/<club-name>/enquire-now/
const CLUB_ENQUIRE_NOW_REGEX = /^\/clubs\/[\w-]+\/enquire-now\/$/g;

const shouldUseInlineOptimizelyScript = (pageContext: PageContext) => {
  const isMeridianBuild = pageContext.buildType === BuildType.MERIDIAN;

  const isGlobalEnquireNowOrMembershipsPage = ['/global-enquire-now/', '/memberships/'].includes(
    pageContext.untranslatedPath!
  );
  const isClubEnquireNowPage = !!pageContext.untranslatedPath?.match(CLUB_ENQUIRE_NOW_REGEX);

  return isMeridianBuild && (isGlobalEnquireNowOrMembershipsPage || isClubEnquireNowPage);
};

const PageLayout = ({ props, children }: LayoutProps) => {
  const pageContext =
    (props.pageContext as TemplatePageContext).baseContext ?? (props.pageContext as PageContext);

  const pageLayout: PageLayoutOptions =
    props.location.pathname === '/admin/' ? { type: PageLayoutType.blank } : pageContext.pageLayout;

  const page: ReactElement = (() => {
    switch (pageLayout.type) {
      case PageLayoutType.blank:
        return <>{children}</>;
      case PageLayoutType.simple:
        return (
          <SimplePageLayout shouldShowFooter={pageLayout.shouldShowFooter ?? false}>
            {children}
          </SimplePageLayout>
        );
      case PageLayoutType.global:
        return (
          <GlobalPageLayout
            shouldShowFooter={pageLayout.shouldShowFooter ?? true}
            snippets={{
              useInlineOptimizelyScript: shouldUseInlineOptimizelyScript(pageContext),
            }}
          >
            {children}
          </GlobalPageLayout>
        );
      case PageLayoutType.club:
        return <ClubPageLayout siteId={pageContext.clubId!}>{children}</ClubPageLayout>;
      case PageLayoutType.careers:
        return <CareerPageLayout shouldShowFooter={true}> {children}</CareerPageLayout>;
    }
  })();

  return (
    <>
      <Helmet>
        <meta name="format-detection" content="telephone=no" />
      </Helmet>
      <SiteProvider pageContext={pageContext}>{page}</SiteProvider>
    </>
  );
};

export default PageLayout;
